import axios from 'axios';
import { message } from 'antd';
import store from '../store';
import { pageModule, hasPermission } from '../permissions/usePermissions';

/**
 * Configure global axios interceptors for the application.
 * 
 * This sets up:
 * - Authentication headers (Bearer token)
 * - Archive view mode header (X-Include-Archived) for users with archive access
 * 
 * Call this function once when the app initializes.
 */
export function setupAxiosInterceptors() {
  // Request interceptor to add headers
  axios.interceptors.request.use(
    (config) => {
      const state = store.getState();
      const [pathname, search = ''] = window.location.hash.slice(1).split('?');
      let context = pageModule(pathname, search);
      if (context === 'preparation') context = ['preparation', 'control', 'collection', 'deliveryNotes'].find(id => hasPermission(state.user, id));
      if (context) config.headers['X-Workflow-Module'] = context;
      
      // Add authentication token if available
      if (state.token) {
        config.headers.Authorization = `Bearer ${state.token}`;
      }
      
      // Add archive view mode header for users with archive access
      // This tells the backend to bypass the isCold filter and show all records
      if (state.archiveViewMode && hasPermission(state.user, 'archives')) {
        config.headers['X-Include-Archived'] = 'true';
        console.log('[Axios] 📦 Archive mode header added to request:', config.url);
      }
      
      // Debug: log archive mode state
      console.log('[Axios] archiveViewMode:', state.archiveViewMode, 'IsManager:', state.user?.IsManager);
      
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor for error handling
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 403) {
        message.warning({ key: 'permission-denied', content: 'Accès refusé : vous n’avez pas la permission nécessaire.' });
        window.dispatchEvent(new Event('permissions-refresh'));
      }
      // A denied permission never invalidates a session. Ignore stale responses
      // from requests sent before the current login as well.
      const token = store.getState().token;
      if (error.response?.status === 401 && token && error.config?.headers?.Authorization === `Bearer ${token}`) {
        store.dispatch({ type: 'LOGOUT' });
      }
      return Promise.reject(error);
    }
  );
}

export default axios;
